import { useNavigate, useSearchParams } from 'react-router-dom'
import { DollarSign, ChevronLeft, ChevronRight } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/Button'
import { useBatches } from '@/features/payroll/hooks/usePayrollBatches'
import { StatusBadge } from './StatusBadge'
import type { OrgPayrollBatch, BatchStatus } from '@/features/payroll/types/payroll.types'

const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i).toLocaleString(undefined, { month: 'short' }),
)

const STATUS_BG: Record<string, string> = {
  paid: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
  approved: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
  consolidated: 'bg-teal-50 border-teal-200 dark:bg-teal-900/20 dark:border-teal-800',
  pending: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
  rejected: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
  void: 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',
}

export function OrgPayrollOverviewPage(): React.ReactElement {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const year = Number(searchParams.get('year')) || new Date().getFullYear()

  const { data } = useBatches(1, 100)
  const batches = data?.data ?? []

  // Index batches by month for selected year
  const batchByMonth = new Map<number, OrgPayrollBatch>()
  for (const b of batches) {
    if (b.payrollMonth.year === year && b.status !== 'void') {
      batchByMonth.set(b.payrollMonth.month, b)
    }
  }

  const recentBatches = batches
    .filter((b) => b.status !== 'void')
    .slice(0, 6)

  // YTD stats
  const yearBatches = batches.filter(
    (b) => b.payrollMonth.year === year && b.status === 'paid',
  )
  const ytdDisbursed = yearBatches.reduce((s, b) => s + (b.totals?.netPay ?? 0), 0)
  const paidCount = yearBatches.length
  const pendingCount = batches.filter(
    (b) => b.payrollMonth.year === year && !['paid', 'void'].includes(b.status),
  ).length

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Payroll' }]}
      icon={DollarSign}
      title="Payroll Overview"
      description={`${year} overview`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchParams({ year: String(year - 1) })}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{year}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchParams({ year: String(year + 1) })}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          }
        />

        <div className="space-y-8">
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">YTD Disbursed</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {ytdDisbursed.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Batches Paid</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {paidCount}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">In Progress</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {pendingCount}
            </p>
          </div>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {MONTH_NAMES.map((name, i) => {
            const month = i + 1
            const batch = batchByMonth.get(month)
            const status = batch?.status as BatchStatus | undefined
            const bgClass = status ? STATUS_BG[status] ?? '' : ''

            return (
              <button
                key={month}
                type="button"
                className={`flex flex-col items-center rounded-lg border p-4 text-center transition-shadow hover:shadow-md ${
                  bgClass || 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                }`}
                onClick={() => {
                  if (batch) {
                    navigate(`/payroll/batches/${batch._id}`)
                  } else {
                    navigate(`/payroll/batches/new?year=${year}&month=${month}`)
                  }
                }}
              >
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {name}
                </span>
                {batch ? (
                  <StatusBadge status={batch.status} className="mt-2" />
                ) : (
                  <span className="mt-2 text-xs text-gray-400">No batch</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Recent batches */}
        {recentBatches.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Recent Batches
            </h3>
            <div className="space-y-2">
              {recentBatches.map((b) => {
                const p = `${b.payrollMonth.year}-${String(b.payrollMonth.month).padStart(2, '0')}`
                return (
                  <button
                    key={b._id}
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50"
                    onClick={() => navigate(`/payroll/batches/${b._id}`)}
                  >
                    <span className="font-medium text-gray-900 dark:text-gray-100">{p}</span>
                    <div className="flex items-center gap-3">
                      <span className="tabular-nums text-gray-500">
                        {(b.totals?.netPay ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      <StatusBadge status={b.status} />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {batches.length === 0 && (
          <EmptyState
            icon={DollarSign}
            title="No payroll batches yet"
            description="Start by creating company payroll runs, then create an org batch."
            action={
              <Button size="sm" onClick={() => navigate('/payroll/batches/new')}>
                Create Batch
              </Button>
            }
          />
        )}
        </div>
      </div>
    </PageShell>
  )
}

OrgPayrollOverviewPage.displayName = 'OrgPayrollOverviewPage'
